/**
 * components/team/TeamPrimitives.js
 *
 * Small building blocks for the team screens. Buttons, dialogs and the
 * confirmation are now the dashboard-wide kit (components/ui); the names here
 * stay so existing screens keep importing them, and each is a thin adapter,
 * so there is still ONE button, ONE dialog and ONE confirm in the console.
 * Two house rules travel with them:
 *   1. wide tables get their own overflow-x container (S.tableWrap), never
 *      <Table responsive>, which the theme disables above 992px;
 *   2. no browser confirm(); destructive actions go through ConfirmModal.
 */
import React from "react";
import { S } from "./teamStyles.js";
import { AppButton, AppDialog, AppConfirmDialog } from "../ui/index.js";

export const AVATAR_COLORS = ["#083860", "#7c3aed", "#047857", "#b45309", "#dc2626", "#0369a1", "#be185d"];
export const getAvatarColor = (str = "?") => AVATAR_COLORS[(str.charCodeAt(0) || 0) % AVATAR_COLORS.length];
export const getInitials = (name = "") =>
  name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
export const fmtDate = (d) => (d ? new Date(d).toLocaleString("en-UG", { dateStyle: "medium", timeStyle: "short" }) : "-");
export const apiErrMsg = (e, fallback) => e?.response?.data?.message || e?.message || fallback;

export const Spinner = () => <span style={S.spinner} />;
export const LoadingCenter = ({ text = "Loading..." }) => (
  <div className="lk-loading lk-loading--panel">
    <span className="spinner-border" role="status" aria-hidden="true" />
    <span>{text}</span>
  </div>
);

const ALERT_ICON = { error: "fa fa-exclamation-circle", success: "fa fa-check-circle", warning: "fa fa-exclamation-triangle", info: "fa fa-info-circle" };

export const Alert = ({ type, msg, onClose }) =>
  msg ? (
    <div role={type === "error" ? "alert" : "status"} style={{ ...S.alert(type), display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
      <span style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
        <i className={ALERT_ICON[type] || ALERT_ICON.info} aria-hidden="true" style={{ marginTop: 2 }} />
        <span>{msg}</span>
      </span>
      {onClose && (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onClose}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 15, lineHeight: 1, color: "inherit", padding: 0 }}
        >
          <i className="fa fa-times" aria-hidden="true" />
        </button>
      )}
    </div>
  ) : null;

/** Older variant names, mapped onto the kit's button family. */
const VARIANT = { primary: "primary", ghost: "quiet", danger: "danger", warning: "warning", success: "outline" };
export const BTN = { primary: S.btnPrimary, ghost: S.btnGhost, danger: S.btnDanger, warning: S.btnWarning, success: S.btnSuccess };

export const Btn = ({ variant = "ghost", size, onClick, disabled, children, style, type = "button", icon, loading }) => (
  <AppButton
    variant={VARIANT[variant] || variant}
    size={size === "sm" ? "sm" : "md"}
    onClick={onClick}
    disabled={disabled}
    loading={loading}
    icon={icon}
    type={type}
    style={style}
  >
    {children}
  </AppButton>
);

export const Field = ({ label, hint, error, children, style, htmlFor }) => (
  <div className="lk-field" style={style}>
    <label className="lk-field__label" htmlFor={htmlFor}>{label}</label>
    {children}
    {error ? <span className="lk-field__error">{error}</span> : hint ? <span className="lk-field__hint">{hint}</span> : null}
  </div>
);

/**
 * Kept for screens that still compose their own header and footer inside the
 * shell. New code passes `title` and `footer` to AppDialog directly.
 */
export const ModalShell = ({ onClose, maxWidth = 520, children, busy }) => (
  <AppDialog open onClose={onClose} hideClose busy={busy} size={maxWidth >= 900 ? "xl" : maxWidth >= 700 ? "lg" : maxWidth <= 440 ? "sm" : "md"}>
    {children}
  </AppDialog>
);

export const ModalFooter = ({ children }) => (
  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap", marginTop: 18 }}>{children}</div>
);

/**
 * The replacement for `confirm()`. It takes a consequence line, because the
 * useful question is never "are you sure"; it is "do you understand that this
 * revokes their gate access and their terminal PIN".
 */
export const ConfirmModal = ({ title, consequence, confirmLabel, variant = "danger", busy, onConfirm, onClose }) => (
  <AppConfirmDialog
    open
    title={title}
    consequence={consequence}
    confirmLabel={confirmLabel}
    tone={variant === "primary" ? "primary" : variant === "warning" ? "warning" : "danger"}
    busy={busy}
    onConfirm={onConfirm}
    onClose={onClose}
  />
);
